import { Logger } from '@/shared/logger/logger';
import { EBundleName } from '@/sources';

import { ECompleteEnv, EScriptEnv } from './types';

const getEnvValue = (key: string, defaultValue: string): string =>
  process.env[key] || defaultValue;

const ENV_MAP: Record<ECompleteEnv, EScriptEnv> = {
  [ECompleteEnv.DEV]: EScriptEnv.DEV,
  [ECompleteEnv.INT]: EScriptEnv.INT,
  [ECompleteEnv.LOCAL]: EScriptEnv.LOCAL,
  [ECompleteEnv.PROD]: EScriptEnv.PROD,
  [ECompleteEnv.TEST]: EScriptEnv.TEST,
};

export const CURR_ENV: EScriptEnv =
  ENV_MAP[getEnvValue('VITE_APP_ENV', '').toLowerCase() as ECompleteEnv] ||
  EScriptEnv.PROD;

export const deployConstants = {
  // eslint-disable-next-line no-undef
  BUILD_COMMIT: __BUILD_COMMIT__ || 'unknown',
  // eslint-disable-next-line no-undef
  BUILD_TIME: __BUILD_TIME__ || -1,
  // eslint-disable-next-line no-undef
  BUILD_TIME_STR: __BUILD_TIME_STR__ || 'unknown',
};

// Initialize Logger environment safely
try {
  if (Logger && typeof Logger.setEnvironment === 'function') {
    Logger.setEnvironment(CURR_ENV);
  }
} catch (error) {
  // Ignore errors during initialization
  // eslint-disable-next-line no-console
  console.warn('Unable to set Logger environment:', error);
}

export const IS_DEBUG = getEnvValue('VITE_APP_IS_DEBUG', 'false') === 'true';
export const USE_TEST_DESTINATIONS = getEnvValue(
  'VITE_APP_USE_TEST_DESTINATIONS',
  'false',
);

export const calculateWgsApiUrl = (env: string): string => {
  // Add your URL calculation logic here
  const baseUrls = {
    development: 'https://wgsdev.wgs-static.com',
    integration: 'https://wgsint.wgs-static.com',
    local: 'http://localhost:3000',
    production: 'https://wgs.wgs-static.com',
    // Add other environments as needed
  };

  return (baseUrls as any)[env] || baseUrls.development;
};

export const WGS_API_BASE_URL_FALLBACK = calculateWgsApiUrl(CURR_ENV);

// API Base URLs
export const IP_API_BASE_URL = getEnvValue(
  'VITE_IP_API_URL',
  'https://beta.get-my-ipv6.com/',
);

// Helper function to safely get bundle name
const getBundleName = (): string | undefined => {
  try {
    return Logger?.bundleName;
  } catch {
    return undefined;
  }
};

// Helper function to check if current bundle is a Shopify bundle
const isShopifyBundle = (): boolean => {
  const bundleName = getBundleName();
  return (
    bundleName === EBundleName.SHOPIFY_DEFAULT ||
    bundleName === EBundleName.SHOPIFY_CUSTOM_PIXEL
  );
};

// Helper function to check if environment is test or local
const isTestOrLocalEnv = (): boolean =>
  CURR_ENV === EScriptEnv.TEST || CURR_ENV === EScriptEnv.LOCAL;

// Helper function to get host domain
const getHostDomain = (): string => {
  try {
    return document.location.origin;
  } catch {
    return '';
  }
};

// Helper function to safely get custom domain
const getCustomDomain = (): string | undefined => {
  try {
    return Logger?.wgsCustomDomain;
  } catch {
    return undefined;
  }
};

export const IDENTITY_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_IDENTITY_API_URL', 'https://wgsdev.wgs-static.com');

export const CONFIG_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_CONFIG_API_URL', 'https://wgsdev.wgs-static.com');

export const BLT_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_BLT_API_URL', 'https://wgsdev.wgs-static.com');

// Apply special Shopify host domain logic only to STATIC_API_BASE_URL
export const STATIC_API_BASE_URL = (() => {
  const customDomain = getCustomDomain();
  if (customDomain) return customDomain;

  const defaultUrl = 'https://wgsdev.wgs-static.com';
  const envUrl = getEnvValue('VITE_STATIC_API_URL', defaultUrl);

  // Use host domain for Shopify bundles in non-test/local environments
  if (isShopifyBundle() && !isTestOrLocalEnv()) {
    const hostDomain = getHostDomain();
    return hostDomain || envUrl;
  }

  return envUrl;
})();

// Fallback static API URL that always uses the original logic (not host name)
export const FALLBACK_STATIC_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_STATIC_API_URL', 'https://wgsdev.wgs-static.com');

export const SEO_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_SEO_API_URL', 'https://wgsdev.wgs-static.com');

export const EWS_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_EWS_API_URL', 'https://wgsdev.wgs-static.com');

export const WGS_API_BASE_URL =
  getCustomDomain() ||
  getEnvValue('VITE_WGS_API_URL', WGS_API_BASE_URL_FALLBACK);

export const G_PROXY_BASE_URL = getEnvValue(
  'VITE_G_PROXY_URL',
  'https://gproxy-beta.mableai.workers.dev',
);

export const SHOPIFY_PROXY_PATH = getEnvValue(
  'VITE_SHOPIFY_PROXY_PATH',
  '/apps/ap-dev',
);

// Calculate Shopify proxy URL
export const calculateShopifyProxyUrl = () =>
  getBundleName() === EBundleName.SHOPIFY_DEFAULT
    ? `${document.location.origin}${SHOPIFY_PROXY_PATH}`
    : '';
